import { useRef, useCallback, useState } from "react";
import {
  MotionBox as Box,
  MotionFlex as Flex,
  viewAppearDefault,
} from "utils/motion";
import Arrow from "components/icons/SliderArrow";
import Text from "components/base/Text";
import Container from "components/base/Container";
import PlayButton from "components/blocks/PlayButton";
import QuoteStart from "components/icons/QuoteStart";
import QuoteEnd from "components/icons/QuoteEnd";
import ModalVideo from "components/sections/ModalVideo";
import Image from "components/base/Image";

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";

const variants = { regular: { opacity: 0 }, masked: { opacity: 1 } };

export function QuoteSection({ quote, author, title, video, bg }) {
  const [isMasked, setMasked] = useState(true);
  const callbackMasked = useCallback(() => setMasked(!isMasked), [isMasked]);
  const minH = video ? "100vh" : "100%";
  return (
    <>
      <Box className="keen-slider__slide" position="relative">
        <Flex
          display={["none", null, "flex"]}
          position="absolute"
          w="100%"
          minH={minH}
          justify="center"
          align="center"
        />
        <Box
          w="100%"
          minH={minH}
          position="absolute"
          top={0}
          bg={bg || "rgba(0, 0, 0, 0.2)"}
        />
        <Box
          w="100%"
          minH={minH}
          position="relative"
          variants={variants}
          initial="masked"
          animate={isMasked ? "masked" : "regular"}
        >
          <Container
            minH={minH}
            display="flex"
            alignItems="center"
            pb={video ? 0 : [12, null, 32]}
          >
            <Flex direction="column" align="center">
              {video && (
                <Box display={["none", null, "block"]} mb="xs">
                  <PlayButton onClick={callbackMasked} />
                </Box>
              )}
              <Flex justify="center" direction={["column", null, "row"]}>
                <Flex align="flex-start" pr="xl">
                  <Box display={["block", null, "none"]}>
                    <QuoteStart small />
                  </Box>
                  <Box display={["none", null, "block"]}>
                    <QuoteStart />
                  </Box>
                </Flex>
                <Box w={["100%", "100%", "80%", video ? "60%" : "50%"]}>
                  <Text
                    type="p_xl"
                    w="100%"
                    py={["xxs", null, "sm"]}
                    color="white"
                    textAlign={["left", null, "center"]}
                  >
                    {quote}
                  </Text>
                </Box>
                <Flex align="flex-end" justify="flex-end" pl="xl">
                  <Box display={["block", null, "none"]}>
                    <QuoteEnd small />
                  </Box>
                  <Box display={["none", null, "block"]}>
                    <QuoteEnd />
                  </Box>
                </Flex>
              </Flex>
              <Flex
                align="center"
                justify={["space-between", null, "center"]}
                pt={["xxs", null, "xl"]}
                pb="xl"
                px={["xxs", "xs", "sm", "md"]}
                w="100%"
              >
                <Box>
                  <Text
                    type="title_md"
                    color="white"
                    textAlign={["left", null, "center"]}
                    mb="xxs"
                  >
                    {author}
                  </Text>
                  <Text
                    type="title_sm"
                    color="primary"
                    textAlign={["left", null, "center"]}
                    maxW="360px"
                  >
                    {title}
                  </Text>
                </Box>
                {video && (
                  <Box display={["block", null, "none"]}>
                    <PlayButton onClick={callbackMasked} scale={0.8} />
                  </Box>
                )}
              </Flex>
            </Flex>
          </Container>
        </Box>
      </Box>
      {video && (
        <ModalVideo
          isOpen={!isMasked}
          onClose={callbackMasked}
          videoId={video}
        />
      )}
    </>
  );
}

export default function TestimonialsSection({
  quotes,
  authors,
  titles,
  videos,
  ...props
}) {
  const ref = useRef();
  const [sliderRef, slider] = useKeenSlider({
    initial: 0,
    slidesPerView: 1,
    loop: true,
  });

  if (!quotes?.length) {
    return null;
  }

  return (
    <Flex
      ref={ref}
      bg="black.900"
      position="relative"
      width="100%"
      pt="xxl"
      flexDirection="column"
      justifyContent="space-between"
      {...props}
    >
      <Box
        w="100%"
        minH="100vh"
        style={{ cursor: "grab", overflow: "hidden" }}
        position="relative"
      >
        <Box w="100%" minH="100%" position="absolute" top={0}>
          <Image
            src="/images/home/img_testimonials_blur.jpg"
            bg="black.700"
            objectPosition="center"
            objectFit="cover"
            minHeight={["75vh", null, "100vh"]}
          />
        </Box>
        <Box
          className="navigation-wrapper"
          variants={viewAppearDefault}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
          <div ref={sliderRef} className="keen-slider">
            {quotes.map((quote, index) => (
              <QuoteSection
                key={videos?.[index] ?? index}
                quote={quote}
                author={authors[index]}
                title={titles?.[index]}
                video={videos?.[index]}
              />
            ))}
          </div>
          <Arrow
            left
            onClick={(e) => e.stopPropagation() || slider.current?.prev()}
          />
          <Arrow
            right
            onClick={(e) => e.stopPropagation() || slider.current?.next()}
          />
        </Box>
      </Box>
    </Flex>
  );
}
